// SMI Workshop (W1 Case Rooms) API — thin wrappers over /api/workshop*.
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

export const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', editor: 'Editor', commenter: 'Commenter', viewer: 'Viewer' };
export const ROLE_RANK = { viewer: 0, commenter: 1, editor: 2, admin: 3, owner: 4 };
export const atLeast = (role, min) => (ROLE_RANK[role] ?? -1) >= ROLE_RANK[min];

export const FRIENDLY = {
    feature_off: 'Case rooms are switched off right now.',
    insufficient_tier: 'Creating a workshop needs Analyst membership.',
    workshop_not_found: 'This workshop does not exist, or you are not a member.',
    room_not_found: 'This room does not exist, or you are not a member.',
    role_too_low: 'Your role in this workshop does not allow that.',
    workshop_read_only: "This workshop is read-only until its owner's membership is active again.",
    no_account: 'No SMI account uses that email. Ask them to sign up first.',
    already_member: 'That person is already a member.',
    note_required: 'Add a short note saying why.',
    name_required: 'Give it a name.',
    body_required: 'Write something first.',
    body_too_long: 'That message is too long (4,000 characters max).',
    verify_report_not_public: 'That Verify report is not public. Use one of your own reports, or a public report link.',
    verify_report_not_yours: 'You can only attach your own private reports.',
    legal_hold: 'This workshop is under a legal hold; items cannot be removed.',
    rate_limited: 'Too many changes at once. Try again in a little while.',
    too_many_members: 'This workshop has reached its member limit.',
    too_many_rooms: 'This workshop has reached its room limit.',
    too_many_workshops: 'You have reached the workshop limit.',
    owner_cannot_leave: 'The owner cannot leave their own workshop.',
    retention_days_out_of_range: 'Retention must be between 7 and 3,650 days.',
    only_evidence_can_be_pinned: 'Only Verify reports can be pinned to the evidence board.',
    too_many_pinned: 'The evidence board is full (100 items). Unpin something first.',
};

export function errText(e, fallback = 'Something went wrong.') {
    const d = e?.response?.data?.detail;
    const code = typeof d === 'object' ? d?.error : null;
    return FRIENDLY[code] || (typeof d === 'object' && d?.message) || fallback;
}

export const ws = {
    features: () => api.get('/workshop/features').then((r) => r.data),
    list: () => api.get('/workshops').then((r) => r.data),
    create: (body) => api.post('/workshops', body).then((r) => r.data),
    get: (id) => api.get(`/workshops/${id}`).then((r) => r.data),
    update: (id, body) => api.patch(`/workshops/${id}`, body).then((r) => r.data),
    legalHold: (id, body) => api.post(`/workshops/${id}/legal-hold`, body).then((r) => r.data),
    members: (id) => api.get(`/workshops/${id}/members`).then((r) => r.data.members),
    addMember: (id, body) => api.post(`/workshops/${id}/members`, body).then((r) => r.data),
    changeRole: (id, uid, body) => api.patch(`/workshops/${id}/members/${uid}`, body).then((r) => r.data),
    removeMember: (id, uid, body) => api.post(`/workshops/${id}/members/${uid}/remove`, body).then((r) => r.data),
    events: (id) => api.get(`/workshops/${id}/events`).then((r) => r.data.events),
    evidence: (id) => api.get(`/workshops/${id}/evidence`).then((r) => r.data.items),
    createRoom: (id, body) => api.post(`/workshops/${id}/rooms`, body).then((r) => r.data.room),
    items: (roomId) => api.get(`/workshop/rooms/${roomId}/items`).then((r) => r.data.items),
    post: (roomId, body) => api.post(`/workshop/rooms/${roomId}/items`, body).then((r) => r.data.item),
    pin: (itemId, pinned) => api.post(`/workshop/items/${itemId}/pin`, { pinned }),
    removeItem: (itemId, note) => api.post(`/workshop/items/${itemId}/remove`, { note }),
};

// GET /workshop/features once per page load; null until known, then the
// server's answer ({ enabled, tier_required, ... }). Off on any error.
let featuresPromise = null;
export function loadWorkshopFeatures() {
    if (!featuresPromise) featuresPromise = ws.features().catch(() => ({ enabled: false }));
    return featuresPromise;
}
export function useWorkshopFeatures() {
    const [f, setF] = useState(null);
    useEffect(() => { let alive = true; loadWorkshopFeatures().then((x) => { if (alive) setF(x); }); return () => { alive = false; }; }, []);
    return f;
}
